export type TParsedMetaText = {
    /** The numeric part of the string, exactly as it appears (without sign), e.g. "1000.3". */
    amount: string;
    /** The trailing text part of the string, exactly as it appears, e.g. "usd". */
    currency: string;
};

/**
 * Parses a meta text string of the shape "<sign?> <number> <text>" into its number and text parts.
 *
 * The string may start with an optional `+` or `-` sign (optionally followed by spaces),
 * then a number, then some trailing text (typically a currency code).
 *
 * Examples:
 *  - "+ 1000.3 usd" -> { amount: "1000.3", currency: "usd" }
 *  - "-5.5usd"      -> { amount: "5.5", currency: "usd" }
 *  - "1000 EUR"     -> { amount: "1000", currency: "EUR" }
 *
 * The returned parts preserve the exact substrings from the input so that they can be matched
 * against the original string by downstream text transformations.
 *
 * @param metaText The raw meta text string. Can be undefined.
 * @returns The parsed amount and currency. Both default to empty strings when nothing can be parsed.
 */
export function parseMetaText(metaText?: string): TParsedMetaText {
    if (!metaText) {
        return { amount: '', currency: '' };
    }

    const match = metaText.match(/^\s*[+-]?\s*([\d.,]+)\s*(.*?)\s*$/);

    if (!match) {
        return { amount: '', currency: '' };
    }

    const [, amount = '', currency = ''] = match;

    return { amount, currency };
}
